import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { CircleAlert, Inbox, Lock, Plus, ShieldCheck, Timer } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  DataTable, DateValue, FormDialog, PageHeader, StatCard, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import {
  useHelpdeskMutations, useHelpdeskSummary, useTicketCategories, useTickets,
} from '../api'
import { TICKET_PRIORITY_LABELS, TICKET_STATUS_LABELS } from '../types'
import type { TicketPriority, TicketStatus, TicketView } from '../types'

const STATUS_TONE: Record<TicketStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  NEW: 'info',
  ASSIGNED: 'info',
  IN_PROGRESS: 'warning',
  WAITING: 'neutral',
  RESOLVED: 'success',
  CLOSED: 'neutral',
  CANCELLED: 'neutral',
}

/** The queue: what is open, what is late, and what nothing was promised about. */
export function HelpdeskQueuePage() {
  useBreadcrumb()
  const navigate = useNavigate()

  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [sort, setSort] = useState<string | undefined>('createdAt,desc')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [categoryId, setCategoryId] = useState('')

  const tickets = useTickets({ page, size, sort, q: search, status, categoryId })
  const summary = useHelpdeskSummary()
  const categories = useTicketCategories()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { raise } = useHelpdeskMutations()

  const [draft, setDraft] = useState<{
    categoryId: string; subject: string; description: string; priority: TicketPriority
    aboutEmployeeId: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const columns = useMemo<ColumnDef<TicketView, unknown>[]>(() => [
    {
      id: 'ticketNo',
      header: 'Ticket',
      accessorFn: (row) => row.ticketNo,
      cell: ({ row }) => (
        <button
          type="button"
          className="font-mono underline underline-offset-2"
          onClick={() => { void navigate(`/helpdesk/${row.original.id}`) }}
        >
          {row.original.ticketNo}
        </button>
      ),
    },
    {
      id: 'subject',
      header: 'Subject',
      accessorFn: (row) => row.subject,
      cell: ({ row }) => (
        <span className="flex items-center gap-2">
          {row.original.confidential && (
            <Lock className="size-3.5 text-slate-500" aria-label="Confidential" />
          )}
          {row.original.subject}
        </span>
      ),
    },
    { id: 'categoryName', header: 'Category', accessorFn: (row) => row.categoryName ?? '—' },
    {
      id: 'priority',
      header: 'Priority',
      accessorFn: (row) => TICKET_PRIORITY_LABELS[row.priority as TicketPriority] ?? row.priority,
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row.status,
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.status}
          tone={STATUS_TONE[row.original.status as TicketStatus] ?? 'neutral'}
          label={TICKET_STATUS_LABELS[row.original.status as TicketStatus] ?? row.original.status}
        />
      ),
    },
    { id: 'assigneeName', header: 'With', accessorFn: (row) => row.assigneeName ?? 'Nobody' },
    {
      id: 'resolutionDueAt',
      header: 'Due',
      accessorFn: (row) => row.resolutionDueAt,
      cell: ({ row }) => row.original.resolutionDueAt == null
        ? <span className="text-slate-500" title={row.original.slaNote}>No due time</span>
        : (
          <span className={row.original.resolutionBreached ? 'font-medium text-rose-700' : ''}>
            <DateValue value={row.original.resolutionDueAt} />
          </span>
        ),
    },
  ], [navigate])

  return (
    <div className="space-y-4">
      <PageHeader
        title="Helpdesk"
        subtitle="What people have asked for, what was promised, and what is late."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                categoryId: categories.data?.[0]?.id ?? '', subject: '', description: '',
                priority: 'NORMAL', aboutEmployeeId: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            Raise a ticket
          </Button>
        }
      />

      {summary.isSuccess && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Open" value={summary.data.open} icon={Inbox} />
            <StatCard label="Nobody on it" value={summary.data.unassigned} icon={ShieldCheck} />
            <StatCard
              label="Past its reply time" value={summary.data.breachedResponse} icon={Timer}
              tint={summary.data.breachedResponse > 0 ? 'orange' : undefined}
            />
            <StatCard
              label="Past its resolution time" value={summary.data.breachedResolution}
              icon={CircleAlert}
              tint={summary.data.breachedResolution > 0 ? 'red' : undefined}
            />
          </div>
          <Card className="space-y-1 p-4 text-sm text-slate-700">
            <p>
              {summary.data.averageSatisfaction == null
                ? 'No satisfaction score yet.'
                : `Satisfaction ${summary.data.averageSatisfaction.toFixed(1)} of 5 across ${summary.data.rated} rating(s).`}
              {summary.data.reopened > 0 && ` ${summary.data.reopened} reopening(s) so far.`}
            </p>
            <p>{summary.data.note}</p>
          </Card>
        </>
      )}

      <DataTable<TicketView>
        columns={columns}
        data={tickets.data}
        isLoading={tickets.isPending}
        error={tickets.error}
        onRetry={() => void tickets.refetch()}
        page={page}
        size={size}
        sort={sort}
        search={search}
        onPageChange={setPage}
        onSizeChange={setSize}
        onSortChange={setSort}
        onSearchChange={(value) => { setSearch(value); setPage(0) }}
        emptyTitle="Nothing is in the queue"
        emptyDescription="A category decides what is promised about a ticket and who is told when it is late. Nothing here is seeded."
        toolbar={
          <>
            <Select
              aria-label="Status"
              value={status}
              onChange={(event) => { setStatus(event.target.value); setPage(0) }}
            >
              <option value="">Any status</option>
              {Object.entries(TICKET_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
            <Select
              aria-label="Category"
              value={categoryId}
              onChange={(event) => { setCategoryId(event.target.value); setPage(0) }}
            >
              <option value="">Any category</option>
              {categories.data?.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </Select>
          </>
        }
      />

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setError(null) } }}
        size="lg"
        title="Raise a ticket"
        description="The due times come from the category, and are fixed the moment this is raised."
        submitLabel="Raise it"
        loading={raise.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              const created = await raise.mutateAsync({
                legalEntityId: entities.data?.[0]?.id ?? null,
                categoryId: draft.categoryId,
                subject: draft.subject,
                description: draft.description || null,
                priority: draft.priority,
                aboutEmployeeId: draft.aboutEmployeeId || null,
              })
              setDraft(null)
              void navigate(`/helpdesk/${created.id}`)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That ticket could not be raised')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="ticket-category">Category</Label>
              <Select
                id="ticket-category"
                className="w-full"
                value={draft.categoryId}
                onChange={(event) => setDraft({ ...draft, categoryId: event.target.value })}
              >
                <option value="">Choose a category…</option>
                {categories.data?.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </Select>
              {draft.categoryId && (
                <p className="mt-1 text-xs text-slate-600">
                  {categories.data?.find((category) => category.id === draft.categoryId)?.slaNote}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="ticket-priority">Priority</Label>
              <Select
                id="ticket-priority"
                className="w-full"
                value={draft.priority}
                onChange={(event) =>
                  setDraft({ ...draft, priority: event.target.value as TicketPriority })}
              >
                {Object.entries(TICKET_PRIORITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="ticket-subject">Subject</Label>
              <Input
                id="ticket-subject" value={draft.subject}
                onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="ticket-description">What is wrong</Label>
              <Input
                id="ticket-description" value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="ticket-about">About somebody else</Label>
              <EmployeePicker
                  id="ticket-about"
                  value={draft.aboutEmployeeId}
                  onChange={(employeeId) => setDraft({ ...draft, aboutEmployeeId: employeeId ?? '' })}
                />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
